import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Button, Chip, Divider, Grid2, IconButton, Paper, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, } from '@mui/material';
import { IoMdClose } from 'react-icons/io';
import { useParams } from 'react-router-dom';
import PaginationComponent from 'components/ui-components/PaginationComponent';
import TableDataGrid from 'components/grid/TableDataGrid';
import moment from 'moment';
import ExportToExcel from "../ExportToExcel";
import ExportToPDF from "../ExportToPDF";
import { useGetAllSafetyQuery } from "store/slices/apis/safetyPackageSlice";


const StyledTableHead = styled(TableCell)(({ theme }) => ({
    fontWeight: "400", fontSize: '12px', color: '#37352F', padding: '10px 10px 10px 15px', lineHeight: '100%'
}));

const StyledTableRow = styled(TableCell)(({ theme }) => ({
    fontWeight: "400", fontSize: '12px', color: '#000000', padding: '12px 12px 12px 15px', lineHeight: '100%',
}));

export default function SafetyPopup({ title, value, status, onClose, onOpen }) {

    const theme = useTheme();
    const { projectId } = useParams();
    const [pageNumber, setPageNumber] = React.useState(1);

    let searchparams = {
        ProjectId: projectId,
        sortBy: "createddate",
        sortOrder: "desc",
        approved_status: "Approved",
        page: pageNumber,
        pageSize: 10,
    }

    if (status == 'dateRange') {
        const [from, to] = value.split(',');
        searchparams = {
            ...searchparams,
            from: from,
            to: to,
        }
    } else if (status === "Safety-kpi") {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth(); // 0-based index

        const fromDate = new Date(year, month, 1);
        const toDate = new Date(year, month + 1, 0);

        // Manual formatting to prevent timezone issues
        const formatDate = (date) => {
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
            const dd = String(date.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        };

        searchparams = {
            ...searchparams,
            from: formatDate(fromDate),
            to: formatDate(toDate),
        };
    }
    else {
        searchparams = {
            ...searchparams,
        }
    }

    const { data: openCloseData = {}, error: openCloseError, isLoading: isOpenCloseLoading } = useGetAllSafetyQuery(searchparams);

    const columns = [
        {
            name: "Safety ID",
            field: "safety_number",
            renderer(params) {
                return (
                    <Stack direction="row" spacing={1}>
                        {params?.safety_number && (
                            <Chip
                                label={params?.safety_number}
                                size="small"
                                sx={{
                                    fontSize: "12px",
                                    "& .MuiChip-label": {
                                        paddingLeft: "8px",
                                        paddingRight: "8px",
                                        fontWeight: 600,
                                    },
                                }}
                            />
                        )}
                    </Stack>
                );
            },
            renderExport(row) {
                return row?.safety_number || "";
            }
        },
        {
            name: "Workflow Status",
            field: "approved_status",
            renderer(params) {
                return (
                    <>
                        {params?.approved_status === "Pending_for_Approval"
                            ? "Pending for approval"
                            : params?.approved_status}
                    </>
                );
            },
            renderExport(row) {
                return row?.approved_status === "Pending_for_Approval"
                    ? "Pending for approval"
                    : row?.approved_status || "";
            }
        },
        {
            name: "Consultant/Contractor",
            field: "consultant_constractor",
            renderExport(row) {
                return row?.consultant_constractor || "";
            }
        },
        {
            name: "Status",
            field: "status",
            renderExport(row) {
                return row?.status || "";
            }
        },
        {
            name: "Created Date",
            field: "createddate",
            renderer(params) {
                return (
                    <>
                        {params?.createddate
                            ? moment(params.createddate).format("DD/MM/YY")
                            : "N.A."}
                    </>
                );
            },
            renderExport(row) {
                return row?.createddate
                    ? moment(row.createddate).format("DD/MM/YY")
                    : "";
            }
        },
        {
            name: " Date",
            field: "date",
            renderer(params) {
                return (
                    <>
                        {params?.date
                            ? moment(params.date).format("DD/MM/YY")
                            : "N.A."}
                    </>
                );
            },
            renderExport(row) {
                return row?.date
                    ? moment(row.date).format("DD/MM/YY")
                    : "";
            }
        },
        {
            name: "Labour Man Hours",
            field: "labour_man_hours",
            renderExport(row) {
                return row?.labour_man_hours || "";
            }
        },
        {
            name: "Manhours Since Last LTA LTI",
            field: "manhours_since_last_lta_lti",
            renderExport(row) {
                return row?.manhours_since_last_lta_lti || "";
            }
        },
        {
            name: "Near Miss Incident",
            field: "near_miss_incident",
            renderExport(row) {
                return row?.near_miss_incident || "";
            }
        },
        {
            name: "LTAs LTIs",
            field: "ltas_ltis",
            renderExport(row) {
                return row?.ltas_ltis || "";
            }
        },
        {
            name: "Fatalities",
            field: "fatalities",
            renderExport(row) {
                return row?.fatalities || "";
            }
        },
    ];


    return (
        <React.Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={'lg'}
                open={onOpen}
                onClose={onClose}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                sx={{
                    '& .MuiDialog-paper': {
                        overflowY: 'inherit'
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: '20px', color: '#696969' }}>{title}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                >
                    <IoMdClose style={{ fontSize: '20px' }} />
                </IconButton>
                <Divider sx={{ w: 1 }} />
                <DialogContent>
                    <Box sx={{ width: '100%' }}>
                        <Grid2 container spacing={2}>
                            <Grid2 container spacing={2} size={12}>
                                <Grid2 xs={12} sm={6}>
                                    <ExportToExcel data={openCloseData?.data?.filter((safety) => safety?.active === true)} columns={columns} fileName="clearance_export" />
                                </Grid2>
                                <Grid2 xs={12} sm={6}>
                                    <ExportToPDF data={openCloseData?.data?.filter((safety) => safety?.active === true)} columns={columns} fileName="clearance_export" />
                                </Grid2>
                            </Grid2>
                            <Grid2 size={12}>
                                <TableDataGrid columns={columns} data={openCloseData?.data?.filter((safety) => safety?.active === true)} isLoading={isOpenCloseLoading} />

                                <Box sx={{ display: "flex", justifyContent: "end" }}>
                                    <PaginationComponent
                                        pageNumber={openCloseData?.pagination?.currentPage || 1}
                                        totalPages={openCloseData?.pagination?.totalPages || 1}
                                        onPageChange={setPageNumber}
                                    />
                                </Box>

                            </Grid2>
                        </Grid2>
                    </Box>
                </DialogContent>
            </Dialog>
        </React.Fragment >
    );
}